import { SelfieSegmentation } from '@mediapipe/selfie_segmentation';

class VirtualBackgroundProcessor {
  constructor() {
    this.segmentation = null;
    this.canvas = null;
    this.ctx = null;
    this.backgroundCanvas = null;
    this.backgroundCtx = null;
    this.backgroundImage = null;
    this.backgroundType = 'none';
    this.isRunning = false;
    this.animationFrameId = null;
    this.videoElement = null;
    this.onFrameCallback = null;
    this.isStopping = false;
  }

  async initialize() {
    console.log('🎨 Initializing virtual background processor...');

    this.segmentation = new SelfieSegmentation({
      locateFile: (file) => {
        return `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`;
      }
    });

    this.segmentation.setOptions({
      modelSelection: 1, // 1 for better quality (landscape mode)
      selfieMode: true,
    });

    this.segmentation.onResults(this.onResults.bind(this));

    // Create canvases for processing
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });

    this.backgroundCanvas = document.createElement('canvas');
    this.backgroundCtx = this.backgroundCanvas.getContext('2d');

    console.log('✅ Virtual background processor initialized');
  }

  onResults(results) {
    if (!this.canvas || !this.ctx || !results) return;

    const { segmentationMask, image } = results;

    // Set canvas size to match video
    if (this.canvas.width !== image.width || this.canvas.height !== image.height) {
      this.canvas.width = image.width;
      this.canvas.height = image.height;
      this.backgroundCanvas.width = image.width;
      this.backgroundCanvas.height = image.height;
    }

    // Clear canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.save();

    // Create blurred background
    if (this.backgroundType === 'blur') {
      // Draw original image with blur
      this.backgroundCtx.filter = 'blur(15px)';
      this.backgroundCtx.drawImage(image, 0, 0, this.canvas.width, this.canvas.height);
      this.backgroundCtx.filter = 'none';
    } else if (this.backgroundType === 'image' && this.backgroundImage) {
      // Draw background image
      this.backgroundCtx.drawImage(this.backgroundImage, 0, 0, this.canvas.width, this.canvas.height);
    } else if (this.backgroundType === 'gradient') {
      // Draw gradient
      const gradient = this.backgroundCtx.createLinearGradient(0, 0, this.canvas.width, this.canvas.height);
      gradient.addColorStop(0, '#667eea');
      gradient.addColorStop(1, '#764ba2');
      this.backgroundCtx.fillStyle = gradient;
      this.backgroundCtx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    }

    // Draw the background
    if (this.backgroundType !== 'none') {
      this.ctx.drawImage(this.backgroundCanvas, 0, 0);
    }

    // Only overwrite existing pixels with the person
    this.ctx.globalCompositeOperation = 'destination-out';
    this.ctx.drawImage(segmentationMask, 0, 0, this.canvas.width, this.canvas.height);

    // Draw the person
    this.ctx.globalCompositeOperation = 'destination-atop';
    this.ctx.drawImage(image, 0, 0, this.canvas.width, this.canvas.height);

    this.ctx.restore();

    // Trigger callback if set
    if (this.onFrameCallback) {
      this.onFrameCallback();
    }
  }

  async start(videoElement, backgroundType = 'none', backgroundValue = null) {
    if (!this.segmentation) {
      await this.initialize();
    }

    this.videoElement = videoElement;
    this.backgroundType = backgroundType;

    if (backgroundType === 'image' && backgroundValue) {
      this.backgroundImage = new Image();
      this.backgroundImage.crossOrigin = 'anonymous';
      this.backgroundImage.src = backgroundValue;
    }

    this.isRunning = true;
    this.processFrames();

    console.log('🎬 Started background processing:', backgroundType);
  }

  async processFrames() {
    if (!this.isRunning || !this.videoElement || !this.segmentation) {
      return;
    }

    try {
      await this.segmentation.send({ image: this.videoElement });
    } catch (err) {
      console.error('Error processing frame:', err);
    }

    // Continue processing
    if (this.isRunning) {
      this.animationFrameId = requestAnimationFrame(() => this.processFrames());
    }
  }

  setBackground(type, value) {
    console.log('🎨 Setting background:', type);
    this.backgroundType = type;

    if (type === 'image' && value) {
      this.backgroundImage = new Image();
      this.backgroundImage.crossOrigin = 'anonymous';
      this.backgroundImage.src = value;
    }
  }

  getCanvasStream(frameRate = 30) {
    if (!this.canvas) return null;
    return this.canvas.captureStream(frameRate);
  }

  async stop() {
    if (this.isStopping) {
      return;
    }

    console.log('🛑 Stopping background processor...');
    this.isStopping = true;
    this.isRunning = false;

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    const segmentationToClose = this.segmentation;
    this.segmentation = null;

    if (segmentationToClose) {
      try {
        await segmentationToClose.close();
      } catch (err) {
        console.warn('Error closing segmentation (already closed):', err);
      }
    }

    this.videoElement = null;
    this.isStopping = false;
  }
}

export default VirtualBackgroundProcessor;
